/**
 * PhysicsDiagram.jsx
 * Renders a 2D SVG situation diagram for each decision-point kind.
 * Three variants: curve, hilltop, jump.
 * Uses live game values (g, r, slope, mass) for labels.
 */

const fmt = (n, d = 1) =>
  n === undefined || isNaN(n) ? "—" : Number(n).toFixed(d);

// ─── Shared helpers ───────────────────────────────────────────────

function Arrow({ x1, y1, x2, y2, color = "#ffd23f", strokeWidth = 2, headSize = 8 }) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return null;
  const ux = dx / len;
  const uy = dy / len;
  // Perpendicular
  const px = -uy;
  const py = ux;
  // Arrowhead base
  const bx = x2 - ux * headSize;
  const by = y2 - uy * headSize;
  return (
    <g>
      <line
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
      />
      <polygon
        points={`${x2},${y2} ${bx + px * headSize * 0.45},${by + py * headSize * 0.45} ${bx - px * headSize * 0.45},${by - py * headSize * 0.45}`}
        fill={color}
      />
    </g>
  );
}

function Label({ x, y, text, color = "#cfd6e3", size = 12, anchor = "middle", bold = false }) {
  return (
    <text
      x={x} y={y}
      fill={color}
      fontSize={size}
      fontFamily="'JetBrains Mono', monospace"
      fontWeight={bold ? 700 : 400}
      textAnchor={anchor}
      dominantBaseline="middle"
    >
      {text}
    </text>
  );
}

function DashedLine({ x1, y1, x2, y2, color = "#5a6478", strokeWidth = 1 }) {
  return (
    <line
      x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={color} strokeWidth={strokeWidth}
      strokeDasharray="5,4"
    />
  );
}

function DiagramShell({ title, children }) {
  return (
    <div
      data-testid="physics-diagram"
      style={{
        marginTop: 14,
        padding: "10px 0 4px",
        background: "rgba(10, 14, 30, 0.7)",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.08)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: 3,
          color: "#e879f9",
          fontWeight: 700,
          textAlign: "center",
          marginBottom: 4,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        {title}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        {children}
      </div>
    </div>
  );
}

// ─── CURVE DIAGRAM ────────────────────────────────────────────────
// Cart on a banked / curved section of track.
// Shows: curved arc, cart, N (normal) arrow, mg (weight) arrow,
// radius r from center of curvature, angle θ.

function CurveDiagram({ g, r, slope, mass, showEquation = true }) {
  const W = 560;
  const H = 300;
  const cx = 280; // center of curvature
  const cy = 40;
  const R = 180; // visual radius for the arc
  // Cart sits at the bottom of the arc
  const cartAngle = Math.PI / 2; // bottom of arc
  const cartX = cx;
  const cartY = cy + R;

  // Arc from -40° to +40° around bottom
  const arcStartAngle = 50 * (Math.PI / 180);
  const arcEndAngle = 130 * (Math.PI / 180);
  const arcPoints = [];
  const steps = 40;
  for (let i = 0; i <= steps; i++) {
    const a = arcStartAngle + (arcEndAngle - arcStartAngle) * (i / steps);
    arcPoints.push({
      x: cx + R * Math.cos(a),
      y: cy + R * Math.sin(a),
    });
  }
  const arcPath = arcPoints
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  const slopeAngle = slope || 0;
  const slopeDeg = ((slopeAngle * 180) / Math.PI);

  // Normal force: points from cart toward center of curvature (upward here)
  const nLen = 65;
  const nEndY = cartY - nLen;

  // Weight: straight down
  const mgLen = 55;
  const mgEndY = cartY + 16 + mgLen;

  // Friction arrow: tangent along track (leftward)
  const fLen = 40;

  return (
    <DiagramShell title="📐 SITUATION DIAGRAM — CURVED TRACK">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        {/* Background */}
        <rect x={0} y={0} width={W} height={H} fill="#0d1020" rx={8} />

        {/* Track arc */}
        <path d={arcPath} fill="none" stroke="#00ffaa" strokeWidth={4} strokeLinecap="round" />

        {/* Radius line: center to cart */}
        <DashedLine x1={cx} y1={cy} x2={cx} y2={cartY - 14} color="#5a6478" strokeWidth={1.5} />

        {/* Center of curvature marker */}
        <circle cx={cx} cy={cy} r={4} fill="none" stroke="#9aa4b2" strokeWidth={1.5} />
        <circle cx={cx} cy={cy} r={1.5} fill="#9aa4b2" />
        <Label x={cx + 8} y={cy - 12} text="center" color="#9aa4b2" size={10} anchor="start" />

        {/* Radius label */}
        <Label x={cx + 10} y={cy + R / 2 - 10} text={`r = ${fmt(r, 1)} m`} color="#3eb6ff" size={12} anchor="start" bold />

        {/* Cart (rectangle) */}
        <rect
          x={cartX - 20} y={cartY - 14}
          width={40} height={16}
          rx={4}
          fill="#ff4d6d" stroke="#ff8a9b" strokeWidth={1.5}
        />
        <Label x={cartX} y={cartY - 6} text="🚗" size={10} />

        {/* Wheels */}
        <circle cx={cartX - 12} cy={cartY + 3} r={4} fill="#2a2a2a" stroke="#555" strokeWidth={1} />
        <circle cx={cartX + 12} cy={cartY + 3} r={4} fill="#2a2a2a" stroke="#555" strokeWidth={1} />

        {/* Normal force N (upward toward center) */}
        <Arrow x1={cartX} y1={cartY - 18} x2={cartX} y2={nEndY} color="#00ffaa" strokeWidth={2.5} />
        <Label x={cartX + 16} y={nEndY - 2} text="N" color="#00ffaa" size={15} anchor="start" bold />

        {/* Weight mg (straight down) */}
        <Arrow x1={cartX} y1={cartY + 10} x2={cartX} y2={mgEndY} color="#ffd23f" strokeWidth={2.5} />
        <Label x={cartX + 16} y={mgEndY} text="mg" color="#ffd23f" size={15} anchor="start" bold />

        {/* Friction arrow (tangent, leftward) */}
        <Arrow x1={cartX - 24} y1={cartY - 6} x2={cartX - 24 - fLen} y2={cartY - 6} color="#ff8a3d" strokeWidth={2} />
        <Label x={cartX - 24 - fLen - 6} y={cartY - 6} text="f" color="#ff8a3d" size={13} anchor="end" bold />

        {/* Velocity arrow (tangent, rightward) */}
        <Arrow x1={cartX + 24} y1={cartY - 6} x2={cartX + 24 + 50} y2={cartY - 6} color="#ff4d6d" strokeWidth={2} />
        <Label x={cartX + 24 + 54} y={cartY - 6} text="v" color="#ff4d6d" size={14} anchor="start" bold />

        {/* Angle θ arc indicator */}
        {slopeDeg !== 0 && (
          <>
            <path
              d={`M ${cx} ${cy + 30} A 30 30 0 0 ${slopeDeg > 0 ? 1 : 0} ${cx + 30 * Math.sin(Math.abs(slopeAngle))} ${cy + 30 * Math.cos(Math.abs(slopeAngle))}`}
              fill="none" stroke="#e879f9" strokeWidth={1.5}
            />
            <Label x={cx + 36} y={cy + 38} text={`θ = ${fmt(slopeDeg, 1)}°`} color="#e879f9" size={11} anchor="start" />
          </>
        )}

        {/* Legend box */}
        <rect x={12} y={H - 68} width={180} height={58} rx={6} fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
        <Label x={20} y={H - 52} text={`g = ${g} m/s²`} color="#9aa4b2" size={10} anchor="start" />
        <Label x={20} y={H - 38} text={`r = ${fmt(r, 1)} m`} color="#3eb6ff" size={10} anchor="start" />
        <Label x={20} y={H - 24} text={`θ = ${fmt(slopeDeg, 1)}°`} color="#e879f9" size={10} anchor="start" />
        <Label x={100} y={H - 52} text="N ↑  normal" color="#00ffaa" size={10} anchor="start" />
        <Label x={100} y={H - 38} text="mg ↓ weight" color="#ffd23f" size={10} anchor="start" />
        <Label x={100} y={H - 24} text="f ← friction" color="#ff8a3d" size={10} anchor="start" />

        {/* Equation reminder — hidden until reasoning stage is passed */}
        {showEquation && (
          <Label x={W / 2} y={H - 6} text="Find v_max = √(g · r · cos θ)" color="#ffd23f" size={11} bold />
        )}
      </svg>
    </DiagramShell>
  );
}

// ─── HILLTOP DIAGRAM ──────────────────────────────────────────────
// Cart at the crest of a hill. Center of curvature is BELOW.
// N and mg both point downward, but N can reach 0 if v too high.

function HilltopDiagram({ g, r, slope, mass, showEquation = true }) {
  const W = 560;
  const H = 300;
  const cx = 280;
  const hillTopY = 100; // cart sits here
  const centerY = hillTopY + 140; // center of curvature below
  const R = centerY - hillTopY; // visual radius

  // Hill arc (inverted — crest at top)
  const arcPoints = [];
  const steps = 40;
  for (let i = 0; i <= steps; i++) {
    const a = (-Math.PI) + (Math.PI) * (i / steps); // -180° to 0° (top half)
    arcPoints.push({
      x: cx + R * 1.4 * Math.cos(a),
      y: hillTopY + R * 0.4 * Math.sin(a) + R * 0.4,
    });
  }
  const arcPath = arcPoints
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  const slopeDeg = ((slope * 180) / Math.PI);

  // mg arrow (straight down from cart)
  const mgLen = 60;
  // N arrow (also down — away from center, which is below)
  const nLen = 45;

  return (
    <DiagramShell title="📐 SITUATION DIAGRAM — HILL TOP">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <rect x={0} y={0} width={W} height={H} fill="#0d1020" rx={8} />

        {/* Hill arc */}
        <path d={arcPath} fill="none" stroke="#00ffaa" strokeWidth={4} strokeLinecap="round" />

        {/* Ground extensions */}
        <line x1={arcPoints[0].x} y1={arcPoints[0].y} x2={30} y2={arcPoints[0].y + 30} stroke="#00ffaa" strokeWidth={3} />
        <line x1={arcPoints[steps].x} y1={arcPoints[steps].y} x2={W - 30} y2={arcPoints[steps].y + 30} stroke="#00ffaa" strokeWidth={3} />

        {/* Cart at the top */}
        <rect
          x={cx - 22} y={hillTopY - 18}
          width={44} height={18}
          rx={4}
          fill="#ff4d6d" stroke="#ff8a9b" strokeWidth={1.5}
        />
        <Label x={cx} y={hillTopY - 9} text="🚗" size={11} />

        {/* Wheels */}
        <circle cx={cx - 13} cy={hillTopY + 2} r={4} fill="#2a2a2a" stroke="#555" strokeWidth={1} />
        <circle cx={cx + 13} cy={hillTopY + 2} r={4} fill="#2a2a2a" stroke="#555" strokeWidth={1} />

        {/* Center of curvature (below the hill) */}
        <circle cx={cx} cy={centerY} r={4} fill="none" stroke="#9aa4b2" strokeWidth={1.5} />
        <circle cx={cx} cy={centerY} r={1.5} fill="#9aa4b2" />
        <Label x={cx + 10} y={centerY + 2} text="center" color="#9aa4b2" size={10} anchor="start" />

        {/* Radius dashed line */}
        <DashedLine x1={cx} y1={hillTopY + 6} x2={cx} y2={centerY - 6} color="#5a6478" strokeWidth={1.5} />
        <Label x={cx + 10} y={(hillTopY + centerY) / 2} text={`r = ${fmt(r, 1)} m`} color="#3eb6ff" size={12} anchor="start" bold />

        {/* Weight mg — straight down (left of center) */}
        <Arrow x1={cx - 14} y1={hillTopY + 8} x2={cx - 14} y2={hillTopY + 8 + mgLen} color="#ffd23f" strokeWidth={2.5} />
        <Label x={cx - 30} y={hillTopY + 8 + mgLen + 2} text="mg" color="#ffd23f" size={14} anchor="end" bold />

        {/* Normal N — toward center (also down, right of center) */}
        <Arrow x1={cx + 14} y1={hillTopY + 8} x2={cx + 14} y2={hillTopY + 8 + nLen} color="#00ffaa" strokeWidth={2.5} />
        <Label x={cx + 30} y={hillTopY + 8 + nLen + 2} text="N" color="#00ffaa" size={14} anchor="start" bold />

        {/* Velocity arrow (horizontal, rightward) */}
        <Arrow x1={cx + 26} y1={hillTopY - 9} x2={cx + 26 + 60} y2={hillTopY - 9} color="#ff4d6d" strokeWidth={2} />
        <Label x={cx + 90} y={hillTopY - 9} text="v" color="#ff4d6d" size={14} anchor="start" bold />

        {/* Danger zone indicator */}
        <rect x={W - 195} y={12} width={183} height={52} rx={8} fill="rgba(255,77,109,0.12)" stroke="rgba(255,77,109,0.4)" strokeWidth={1} />
        <Label x={W - 103} y={26} text="⚠ DANGER ZONE" color="#ff4d6d" size={11} bold />
        <Label x={W - 103} y={42} text="If v too high → N = 0" color="#ff8a9b" size={10} />
        <Label x={W - 103} y={54} text="Cart lifts off the track!" color="#ff8a9b" size={10} />

        {/* Legend box */}
        <rect x={12} y={H - 68} width={200} height={58} rx={6} fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
        <Label x={20} y={H - 52} text={`g = ${g} m/s²`} color="#9aa4b2" size={10} anchor="start" />
        <Label x={20} y={H - 38} text={`r = ${fmt(r, 1)} m`} color="#3eb6ff" size={10} anchor="start" />
        <Label x={20} y={H - 24} text={`θ ≈ ${fmt(slopeDeg, 1)}°`} color="#e879f9" size={10} anchor="start" />
        <Label x={120} y={H - 52} text="mg ↓ weight" color="#ffd23f" size={10} anchor="start" />
        <Label x={120} y={H - 38} text="N ↓ normal" color="#00ffaa" size={10} anchor="start" />
        <Label x={120} y={H - 24} text="v → speed" color="#ff4d6d" size={10} anchor="start" />

        {/* Equation — hidden until reasoning stage is passed */}
        {showEquation && (
          <Label x={W / 2} y={H - 6} text="N = mg·cosθ − mv²/r  →  Find v where N > 0" color="#ffd23f" size={11} bold />
        )}
      </svg>
    </DiagramShell>
  );
}

// ─── JUMP DIAGRAM ─────────────────────────────────────────────────
// Cart at the end of a ramp, projectile trajectory over a gap.

function JumpDiagram({ g, showEquation = true }) {
  const W = 560;
  const H = 300;

  // Ramp
  const rampBaseX = 80;
  const rampBaseY = 200;
  const rampTopX = 160;
  const rampTopY = 140;

  // Landing platform
  const landX = 410;
  const landY = 200;

  // Gap distance label
  const gapMidX = (rampTopX + landX) / 2;

  // Parabolic trajectory points
  const trajPoints = [];
  const numPts = 50;
  const launchAngle = Math.PI / 4; // 45°
  const v0 = 18; // reference speed
  const vx = v0 * Math.cos(launchAngle);
  const vy = v0 * Math.sin(launchAngle);
  const totalFlightT = (2 * vy) / g;
  for (let i = 0; i <= numPts; i++) {
    const t = (i / numPts) * totalFlightT;
    const px = rampTopX + (vx * t / totalFlightT) * (landX - rampTopX);
    const py = rampTopY - (vy * t - 0.5 * g * t * t) / (0.5 * g * totalFlightT * totalFlightT / 4) * 60;
    trajPoints.push({ x: px, y: py });
  }
  const trajPath = trajPoints
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  // Angle arc
  const arcR = 40;

  return (
    <DiagramShell title="📐 SITUATION DIAGRAM — PROJECTILE JUMP">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <rect x={0} y={0} width={W} height={H} fill="#0d1020" rx={8} />

        {/* Ground level */}
        <line x1={20} y1={rampBaseY} x2={rampBaseX} y2={rampBaseY} stroke="#00ffaa" strokeWidth={3} />
        <line x1={landX} y1={landY} x2={W - 20} y2={landY} stroke="#00ffaa" strokeWidth={3} />

        {/* Ramp */}
        <line x1={rampBaseX} y1={rampBaseY} x2={rampTopX} y2={rampTopY} stroke="#00ffaa" strokeWidth={4} strokeLinecap="round" />

        {/* Landing platform top */}
        <rect x={landX - 5} y={landY - 4} width={W - 20 - landX + 25} height={8} rx={2} fill="#00ffaa" opacity={0.3} />

        {/* Gap (chasm) — filled dark */}
        <rect x={rampTopX - 10} y={rampBaseY} width={landX - rampTopX + 20} height={60} fill="rgba(255,77,109,0.08)" stroke="rgba(255,77,109,0.2)" strokeWidth={1} rx={4} />
        <Label x={gapMidX} y={rampBaseY + 30} text="GAP ☠" color="#ff4d6d" size={13} bold />

        {/* Cart on ramp */}
        <rect
          x={rampTopX - 30} y={rampTopY - 20}
          width={36} height={16}
          rx={4}
          fill="#ff4d6d" stroke="#ff8a9b" strokeWidth={1.5}
          transform={`rotate(-36.5, ${rampTopX - 12}, ${rampTopY - 12})`}
        />

        {/* Parabolic trajectory (dotted) */}
        <path d={trajPath} fill="none" stroke="#ffd23f" strokeWidth={2.5} strokeDasharray="6,5" opacity={0.9} />

        {/* Velocity arrow at launch (along ramp direction upward) */}
        <Arrow
          x1={rampTopX} y1={rampTopY}
          x2={rampTopX + 50 * Math.cos(-launchAngle)} y2={rampTopY + 50 * Math.sin(-launchAngle)}
          color="#ff4d6d" strokeWidth={2.5}
        />
        <Label
          x={rampTopX + 55 * Math.cos(-launchAngle) + 4}
          y={rampTopY + 55 * Math.sin(-launchAngle) - 4}
          text="v₀" color="#ff4d6d" size={15} anchor="start" bold
        />

        {/* Gravity arrow at trajectory apex */}
        <Arrow x1={gapMidX} y1={trajPoints[Math.floor(numPts * 0.35)].y + 5} x2={gapMidX} y2={trajPoints[Math.floor(numPts * 0.35)].y + 50} color="#ffd23f" strokeWidth={2} />
        <Label x={gapMidX + 14} y={trajPoints[Math.floor(numPts * 0.35)].y + 36} text="g ↓" color="#ffd23f" size={13} anchor="start" bold />

        {/* Angle θ indicator at ramp base */}
        <path
          d={`M ${rampBaseX + arcR} ${rampBaseY} A ${arcR} ${arcR} 0 0 0 ${rampBaseX + arcR * Math.cos(-launchAngle)} ${rampBaseY + arcR * Math.sin(-launchAngle)}`}
          fill="none" stroke="#e879f9" strokeWidth={2}
        />
        <Label x={rampBaseX + arcR + 14} y={rampBaseY - 14} text="θ = 45°" color="#e879f9" size={12} anchor="start" bold />

        {/* Gap distance R label (horizontal dimension line) */}
        {/* Left tick */}
        <line x1={rampTopX} y1={rampBaseY + 54} x2={rampTopX} y2={rampBaseY + 66} stroke="#3eb6ff" strokeWidth={1.5} />
        {/* Right tick */}
        <line x1={landX} y1={rampBaseY + 54} x2={landX} y2={rampBaseY + 66} stroke="#3eb6ff" strokeWidth={1.5} />
        {/* Horizontal line */}
        <line x1={rampTopX} y1={rampBaseY + 60} x2={landX} y2={rampBaseY + 60} stroke="#3eb6ff" strokeWidth={1.5} />
        {/* Arrowheads */}
        <polygon points={`${rampTopX},${rampBaseY + 60} ${rampTopX + 7},${rampBaseY + 56} ${rampTopX + 7},${rampBaseY + 64}`} fill="#3eb6ff" />
        <polygon points={`${landX},${rampBaseY + 60} ${landX - 7},${rampBaseY + 56} ${landX - 7},${rampBaseY + 64}`} fill="#3eb6ff" />
        <Label x={gapMidX} y={rampBaseY + 74} text="R ≈ 32 m" color="#3eb6ff" size={13} bold />

        {/* Legend box */}
        <rect x={12} y={12} width={175} height={58} rx={6} fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
        <Label x={20} y={28} text={`g = ${g} m/s²`} color="#9aa4b2" size={10} anchor="start" />
        <Label x={20} y={42} text="R ≈ 32 m (gap)" color="#3eb6ff" size={10} anchor="start" />
        <Label x={20} y={56} text="θ = 45° (ramp)" color="#e879f9" size={10} anchor="start" />

        {/* Equation — hidden until reasoning stage is passed */}
        {showEquation && (
          <Label x={W / 2} y={H - 8} text="R = v₀² · sin(2θ) / g  →  Find v₀ = √(R · g)" color="#ffd23f" size={11} bold />
        )}
      </svg>
    </DiagramShell>
  );
}

// ─── BRAKE DIAGRAM (Phase 2 — SUVAT) ─────────────────────────────
function BrakeDiagram({ v0, showEquation = true }) {
  const W = 560, H = 200;
  const lineX = 470;
  return (
    <DiagramShell title="📐 SITUATION — BRAKE ZONE (STOP LINE AHEAD)">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <rect x={0} y={0} width={W} height={H} fill="#0d1020" rx={8} />
        {/* Road */}
        <line x1={20} y1={140} x2={W - 20} y2={140} stroke="#00ffaa" strokeWidth={4} />
        {/* Stop line + barrier */}
        <line x1={lineX} y1={100} x2={lineX} y2={150} stroke="#ff4d6d" strokeWidth={4} />
        <Label x={lineX} y={88} text="STOP LINE" color="#ff4d6d" size={11} bold />
        <rect x={lineX + 14} y={108} width={16} height={32} rx={3} fill="#ff8a3d" />
        <rect x={lineX + 34} y={108} width={16} height={32} rx={3} fill="#ff8a3d" />
        <Label x={lineX + 32} y={98} text="barrels!" color="#ff8a3d" size={10} />
        {/* Cart */}
        <rect x={60} y={118} width={44} height={18} rx={4} fill="#ff4d6d" stroke="#ff8a9b" strokeWidth={1.5} />
        <Arrow x1={110} y1={127} x2={180} y2={127} color="#ffd23f" strokeWidth={2.5} />
        <Label x={190} y={127} text={`u = ${fmt(v0, 1)} m/s`} color="#ffd23f" size={13} anchor="start" bold />
        {/* Braking distance dimension */}
        <DashedLine x1={104} y1={165} x2={lineX} y2={165} color="#3eb6ff" strokeWidth={1.5} />
        <Label x={(104 + lineX) / 2} y={180} text="d = ? (your braking distance)" color="#3eb6ff" size={12} bold />
        {/* Decel arrow */}
        <Arrow x1={300} y1={110} x2={240} y2={110} color="#ff4d6d" strokeWidth={2} />
        <Label x={310} y={110} text="a = 12 m/s² (brakes)" color="#ff8a9b" size={11} anchor="start" />
        {showEquation && (
          <Label x={W / 2} y={H - 6} text="v² = u² − 2·a·d  →  d = u²/(2a)" color="#ffd23f" size={11} bold />
        )}
      </svg>
    </DiagramShell>
  );
}

// ─── ENERGY CREST DIAGRAM (Phase 2 — Energy) ─────────────────────
function CrestDiagram({ g, dh = 5.2, showEquation = true }) {
  const W = 560, H = 220;
  return (
    <DiagramShell title="📐 SITUATION — ENGINE-OFF CLIMB (ENERGY GATE)">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <rect x={0} y={0} width={W} height={H} fill="#0d1020" rx={8} />
        {/* Hill profile */}
        <path d="M 30 170 L 220 170 C 330 170 380 80 480 75 L 530 75"
              fill="none" stroke="#00ffaa" strokeWidth={4} strokeLinecap="round" />
        {/* Cart at base */}
        <rect x={120} y={148} width={44} height={18} rx={4} fill="#ff4d6d" stroke="#ff8a9b" strokeWidth={1.5} />
        <Arrow x1={170} y1={157} x2={235} y2={157} color="#ffd23f" strokeWidth={2.5} />
        <Label x={245} y={157} text="v_min = ?" color="#ffd23f" size={13} anchor="start" bold />
        {/* Height dimension */}
        <DashedLine x1={480} y1={75} x2={480} y2={170} color="#3eb6ff" strokeWidth={1.5} />
        <Label x={495} y={122} text={`Δh ≈ ${fmt(dh, 1)} m`} color="#3eb6ff" size={12} anchor="start" bold />
        {/* Engine off sign */}
        <rect x={250} y={95} width={110} height={26} rx={6} fill="rgba(255,77,109,0.15)" stroke="rgba(255,77,109,0.5)" strokeWidth={1} />
        <Label x={305} y={108} text="⚠ ENGINE OFF" color="#ff8a9b" size={11} bold />
        {/* Energy ledger */}
        <Label x={95} y={120} text="½·m·v²  (KE in)" color="#ffd23f" size={11} />
        <Label x={420} y={55} text="m·g·Δh  (PE out)" color="#3eb6ff" size={11} />
        {showEquation && (
          <Label x={W / 2} y={H - 6} text="½·m·v² = m·g·Δh  →  v_min = √(2·g·Δh)" color="#ffd23f" size={11} bold />
        )}
      </svg>
    </DiagramShell>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────

export default function PhysicsDiagram({ kind, g, r, slope, mass, v0, dh, showEquation = true }) {
  if (kind === "curve") return <CurveDiagram g={g} r={r} slope={slope} mass={mass} showEquation={showEquation} />;
  if (kind === "hilltop") return <HilltopDiagram g={g} r={r} slope={slope} mass={mass} showEquation={showEquation} />;
  if (kind === "jump") return <JumpDiagram g={g} showEquation={showEquation} />;
  if (kind === "brake") return <BrakeDiagram v0={v0} showEquation={showEquation} />;
  if (kind === "energycrest") return <CrestDiagram g={g} dh={dh} showEquation={showEquation} />;
  return null;
}
